import { REALTIME_PROTOCOL_VERSION, isWorldMessage } from "@uzverse/core";
import type {
  Focus,
  Id,
  Locale,
  ModelPreference,
  RealtimeConnection,
  ServerMessage,
} from "@uzverse/core";
import type { WorldChannel } from "../world/store.js";
import type { AppStore } from "../state/store.js";
import { vehicleHub } from "../world/vehicles/hub.js";

export interface RealtimeClientOptions {
  connect: () => RealtimeConnection;
  token: string;
  store: AppStore;
  /** Called when the gateway rejects the session. */
  onUnauthenticated: () => void;
  /** Reconnect delay in ms (grows up to 8x). */
  retryMs?: number;
}

/**
 * Owns one gateway connection: authenticates, de-duplicates by `seq`, reconnects with the last
 * seq so the server resumes without loss or duplication, and feeds the store.
 */
export class RealtimeClient {
  private connection: RealtimeConnection | null = null;
  private unsubscribe: (() => void) | null = null;
  private lastSeq = 0;
  private attempts = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = true;
  private authed = false;
  private world: WorldChannel | null = null;

  constructor(private readonly opts: RealtimeClientOptions) {}

  start(): void {
    this.stopped = false;
    this.open();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.teardown();
  }

  /**
   * Routes the `world.*` messages to `channel` and lets it subscribe after every (re)connect.
   * Returns a function that detaches it (and ends the world subscription).
   */
  attachWorld(channel: WorldChannel): () => void {
    this.world = channel;
    if (this.authed) channel.onOpen((m) => this.connection?.send(m));
    return () => {
      if (this.world !== channel) return;
      this.world = null;
      channel.onClose();
      // Before `auth.ok` there is no subscription to end, and the gateway rejects any other
      // message ahead of the auth handshake (it closes the socket as unauthenticated).
      if (this.authed) this.connection?.send({ type: "world.unsubscribe" });
    };
  }

  private teardown(): void {
    if (this.authed) this.world?.onClose();
    this.authed = false;
    vehicleHub.detach();
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.connection?.close();
    this.connection = null;
  }

  private open(): void {
    this.opts.store.setConnection("connecting");
    const connection = this.opts.connect();
    this.connection = connection;
    this.unsubscribe = connection.onMessage((m) => this.onMessage(m));
    connection.send({
      type: "auth",
      token: this.opts.token,
      protocol: REALTIME_PROTOCOL_VERSION,
      ...(this.lastSeq > 0 ? { lastSeq: this.lastSeq } : {}),
    });
  }

  private onMessage(message: ServerMessage): void {
    // Other drivers' poses (the vehicles backend's `vehicle.pose`) go straight to the vehicle hub.
    if (vehicleHub.ingest(message)) return;
    if (isWorldMessage(message)) {
      this.world?.onMessage(message);
      return;
    }
    if (message.type === "error") {
      if (message.code === "not_implemented" && this.world) {
        this.world.onMessage(message);
        return;
      }
      if (message.code === "unauthenticated") this.opts.onUnauthenticated();
      else if (message.code === "unavailable") this.scheduleReconnect();
      else this.opts.store.setError(message.code);
      return;
    }
    if (message.seq > 0) {
      if (message.seq <= this.lastSeq) return; // Already processed (replay overlap).
      this.lastSeq = message.seq;
    }
    if (message.type === "auth.ok") {
      this.attempts = 0;
      this.authed = true;
      vehicleHub.attach((m) => this.connection?.send(m as never));
      this.world?.onOpen((m) => this.connection?.send(m));
    }
    this.opts.store.apply(message);
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.timer) return;
    this.opts.store.setConnection("offline");
    this.teardown();
    const base = this.opts.retryMs ?? 1000;
    const delay = base * Math.min(2 ** this.attempts, 8);
    this.attempts += 1;
    this.timer = setTimeout(() => {
      this.timer = null;
      if (!this.stopped) this.open();
    }, delay);
  }

  /** Sends a chat message; returns its client message id. */
  sendChat(
    text: string,
    focus: Focus | null,
    locale: Locale,
    modelPreference?: ModelPreference,
  ): string {
    const clientMsgId = crypto.randomUUID();
    this.opts.store.addUserMessage(clientMsgId, text);
    const hasPreference = modelPreference && Object.keys(modelPreference).length > 0;
    this.connection?.send({
      type: "chat.send",
      clientMsgId,
      text,
      focus,
      locale,
      ...(hasPreference ? { modelPreference } : {}),
    });
    return clientMsgId;
  }

  subscribeTask(taskId: Id): void {
    this.connection?.send({ type: "task.subscribe", taskId });
  }

  respondToApproval(approvalId: Id, decision: "approved" | "rejected"): void {
    this.connection?.send({ type: "approval.response", approvalId, decision });
  }
}
